import { useEffect, useRef, type RefObject } from 'react';

export type ClickOutsideTarget = RefObject<HTMLElement | null> | (() => HTMLElement | null);

export function useClickOutside(
    targets: ClickOutsideTarget | ClickOutsideTarget[],
    handler: (event: MouseEvent) => void,
    enabled = true,
): void {
    const stateRef = useRef({ targets, handler });

    useEffect(() => {
        stateRef.current = { targets, handler };
    });

    useEffect(() => {
        if (!enabled) return undefined;

        const listener = (event: MouseEvent): void => {
            const { targets: current, handler: onOutside } = stateRef.current;
            const list = Array.isArray(current) ? current : [current];

            const inside = list.some((target) => {
                const el = typeof target === 'function' ? target() : target?.current;
                return el && el.contains(event.target as Node);
            });

            if (!inside) onOutside(event);
        };

        document.addEventListener('mousedown', listener);
        return () => document.removeEventListener('mousedown', listener);
    }, [enabled]);
}
